import { For, Show, Suspense, createSignal, type ParentComponent } from "solid-js";
import { A, createAsync, revalidate, type RouteDefinition } from "@solidjs/router";
import { NoHydration } from "solid-js/web";
import { AppShell, PageHeader } from "~/components/layout";
import {
  Button, Card, CardContent, CardHeader, CardTitle, Badge, Skeleton, EmptyState,
  Tabs, TabsList, TabsTrigger, TabsContent
} from "~/components/ui";
import { NewAnalysisModal, NewTemplateModal } from "~/components/modals";
import { CACHE_KEYS } from "~/server/bff/cache-keys";
import { getAnalysisRuns, getAnalysisTemplates } from "~/server/bff";
import { formatRelativeTime } from "~/lib/utils";
import {
  Plus, Play, FileText, Sparkles, Clock, CheckCircle, XCircle, Loader2
} from "lucide-solid";

export const route = {
  preload: () => Promise.all([getAnalysisRuns(), getAnalysisTemplates()]),
} satisfies RouteDefinition;

export default function AnalysisLabPage() {
  if (import.meta.env.DEV) {
    console.info("[debug] render AnalysisLabPage");
  }
  const [activeTab, setActiveTab] = createSignal("runs");
  const [showNewAnalysisModal, setShowNewAnalysisModal] = createSignal(false);
  const [showNewTemplateModal, setShowNewTemplateModal] = createSignal(false);
  
  const analysisRuns = createAsync(() => getAnalysisRuns());
  const templates = createAsync(() => getAnalysisTemplates());

  const openNewAnalysisModal = () => setShowNewAnalysisModal(true);
  const openNewTemplateModal = () => setShowNewTemplateModal(true);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <StaticIcon>
            <CheckCircle class="h-4 w-4 text-green-500" />
          </StaticIcon>
        );
      case "failed":
        return (
          <StaticIcon>
            <XCircle class="h-4 w-4 text-red-500" />
          </StaticIcon>
        );
      case "running":
        return (
          <StaticIcon>
            <Loader2 class="h-4 w-4 text-blue-500 animate-spin" />
          </StaticIcon>
        );
      default:
        return (
          <StaticIcon>
            <Clock class="h-4 w-4 text-muted-foreground" />
          </StaticIcon>
        );
    }
  };

  const handleUseTemplate = (templateId: string) => {
    // Open the new analysis modal - it will handle template preselection in future
    setShowNewAnalysisModal(true);
  };

  const handleCreateTemplate = () => {
    setShowNewTemplateModal(true);
  };

  const handleTemplateCreated = () => {
    // Revalidate templates list to show the new template
    revalidate(CACHE_KEYS.ANALYSIS_TEMPLATES);
  };

  return (
    <AppShell title="Analysis Lab">
      <div class="p-6">
        <PageHeader
          title="Analysis Lab"
          description="Extract and synthesize insights across videos"
          actions={
            <Button onClick={openNewAnalysisModal}>
              <StaticIcon>
                <Plus class="h-4 w-4 mr-2" />
              </StaticIcon>
              New Analysis
            </Button>
          }
        />

        <Tabs value={activeTab()} onChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="runs">
              <StaticIcon>
                <Play class="h-4 w-4 mr-1" />
              </StaticIcon>
              Analysis Runs
            </TabsTrigger>
            <TabsTrigger value="templates">
              <StaticIcon>
                <FileText class="h-4 w-4 mr-1" />
              </StaticIcon>
              Templates
            </TabsTrigger>
          </TabsList>

          <TabsContent value="runs" class="mt-6">
            <Suspense fallback={<RunsSkeleton />}>
              <Show when={analysisRuns()} fallback={<RunsSkeleton />}>
                {(items) => (
                  <Show
                    when={items().length > 0}
                    fallback={
                      <EmptyState
                        icon={
                          <StaticIcon>
                            <Sparkles class="h-12 w-12" />
                          </StaticIcon>
                        }
                        title="No analysis runs yet"
                        description="Start a new analysis to extract structured data from your videos"
                        actionLabel="Start Analysis"
                        onAction={openNewAnalysisModal}
                      />
                    }
                  >
                    <div class="space-y-3">
                      <For each={items()}>
                        {(run) => (
                          <A href={`/investigate/analysis/${run.id}`}>
                            <Card class="hover:border-primary/50 transition-colors">
                              <CardContent class="p-4">
                                <div class="flex items-center gap-4">
                                  {getStatusIcon(run.status)}
                                  <div class="flex-1 min-w-0">
                                    <h3 class="font-medium">{run.name}</h3>
                                    <div class="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                      <Badge variant="outline" class="text-xs font-mono">
                                        {run.mode}
                                      </Badge>
                                      <Show when={run.templateName}>
                                        <span>• {run.templateName}</span>
                                      </Show>
                                      <span>• {run.sourceVideoIds.length} videos</span>
                                    </div>
                                  </div>
                                  <div class="text-right text-sm text-muted-foreground">
                                    <p>{formatRelativeTime(run.createdAt)}</p>
                                    <Show when={run.completedAt}>
                                      <p class="text-xs">
                                        Completed {formatRelativeTime(run.completedAt!)}
                                      </p>
                                    </Show>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </A>
                        )}
                      </For>
                    </div>
                  </Show>
                )}
              </Show>
            </Suspense>
          </TabsContent>

          <TabsContent value="templates" class="mt-6">
            <Suspense fallback={<TemplatesSkeleton />}>
              <Show when={templates()} fallback={<TemplatesSkeleton />}>
                {(items) => (
                  <Show when={items().length > 0}>
                    <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <Card 
                        class="border-dashed border-2 hover:border-primary/50 transition-colors cursor-pointer"
                        onClick={handleCreateTemplate}
                      >
                        <CardContent class="p-6 flex flex-col items-center justify-center text-center min-h-[180px]">
                          <StaticIcon>
                            <Plus class="h-8 w-8 text-muted-foreground mb-2" />
                          </StaticIcon>
                          <p class="font-medium">Create Template</p>
                          <p class="text-sm text-muted-foreground">Define a custom extraction schema</p>
                        </CardContent>
                      </Card>

                      <For each={items()}>
                        {(template) => (
                          <Card class="group hover:border-primary/50 transition-colors cursor-pointer">
                            <CardHeader class="pb-2">
                              <div class="flex items-start justify-between">
                                <CardTitle class="text-base">{template.name}</CardTitle>
                                <Badge variant={template.isBuiltIn ? "secondary" : "outline"} class="text-xs">
                                  {template.isBuiltIn ? "Built-in" : "Custom"}
                                </Badge>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <Show when={template.description}>
                                <p class="text-sm text-muted-foreground mb-3">{template.description}</p>
                              </Show>
                              <div class="flex items-center justify-between">
                                <Badge variant="outline" class="text-xs font-mono">
                                  {template.mode}
                                </Badge>
                                <span class="text-xs text-muted-foreground">
                                  {Object.keys(template.schema).length} fields
                                </span>
                              </div>
                              <div class="mt-3 pt-3 border-t opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button 
                                  size="sm" 
                                  class="w-full"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleUseTemplate(template.id);
                                  }}
                                >
                                  <StaticIcon>
                                    <Play class="h-3 w-3 mr-1" />
                                  </StaticIcon>
                                  Use Template
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </For>
                    </div>
                  </Show>
                )}
              </Show>
            </Suspense>
          </TabsContent>
        </Tabs>
      </div>

      <Show when={showNewAnalysisModal()}>
        <Suspense>
          <NewAnalysisModal
            isOpen={showNewAnalysisModal()}
            onClose={() => setShowNewAnalysisModal(false)}
          />
        </Suspense>
      </Show>

      <Show when={showNewTemplateModal()}>
        <Suspense>
          <NewTemplateModal
            isOpen={showNewTemplateModal()}
            onClose={() => setShowNewTemplateModal(false)}
            onCreated={handleTemplateCreated}
          />
        </Suspense>
      </Show>
    </AppShell>
  );
}

function RunsSkeleton() {
  return (
    <div class="space-y-3">
      <For each={[1, 2, 3]}>
        {() => (
          <Card>
            <CardContent class="p-4">
              <div class="flex items-center gap-4">
                <Skeleton class="h-4 w-4 rounded-full" />
                <div class="flex-1 space-y-2">
                  <Skeleton class="h-5 w-48" />
                  <Skeleton class="h-4 w-32" />
                </div>
                <Skeleton class="h-4 w-24" />
              </div>
            </CardContent>
          </Card>
        )}
      </For>
    </div>
  );
}

function TemplatesSkeleton() {
  return (
    <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <For each={[1, 2, 3, 4]}>
        {() => (
          <Card>
            <CardHeader class="pb-2">
              <Skeleton class="h-5 w-32" />
            </CardHeader>
            <CardContent class="space-y-2">
              <Skeleton class="h-4 w-full" />
              <Skeleton class="h-4 w-24" />
            </CardContent>
          </Card>
        )}
      </For>
    </div>
  );
}

const StaticIcon: ParentComponent = (props) => {
  return <NoHydration>{props.children}</NoHydration>;
};
